// TanStack Query hooks. Query keys are centralised in `keys` so that a mutation
// can invalidate exactly what it affected without guessing at key shapes.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query'

import { ApiError, api } from './client'
import type {
  AnswerResult,
  BulkImportResult,
  Direction,
  Entry,
  EntryPage,
  Language,
  LanguageStats,
  PracticeCard,
  User,
  Visibility,
} from './types'

export const keys = {
  me: ['me'] as const,
  languages: ['languages'] as const,
  language: (id: number) => ['language', id] as const,
  entries: (id: number, q: string) => ['entries', id, q] as const,
  publicLanguages: (q: string) => ['public-languages', q] as const,
  stats: (id: number) => ['stats', id] as const,
  practice: (id: number, direction: Direction | 'both') =>
    ['practice', id, direction] as const,
}

// --- auth -----------------------------------------------------------------

export function useMe(options?: Partial<UseQueryOptions<User | null>>) {
  return useQuery<User | null>({
    queryKey: keys.me,
    queryFn: async () => {
      try {
        return await api.get<User>('/auth/me')
      } catch (error) {
        // Not being logged in is an expected state, not a failure to report.
        if (error instanceof ApiError && error.isUnauthorized) return null
        throw error
      }
    },
    // Auth state changes only through explicit login/logout, both of which
    // update the cache directly.
    staleTime: Infinity,
    retry: false,
    ...options,
  })
}

export function useLogin() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { email: string; password: string }) =>
      api.post<User>('/auth/login', body),
    onSuccess: (user) => {
      qc.setQueryData(keys.me, user)
      qc.invalidateQueries({ queryKey: keys.languages })
    },
  })
}

export function useRegister() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: {
      email: string
      password: string
      display_name: string
    }) => api.post<User>('/auth/register', body),
    onSuccess: (user) => qc.setQueryData(keys.me, user),
  })
}

export function useLogout() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<void>('/auth/logout'),
    // Drop everything: the whole cache belonged to the user who just left.
    onSuccess: () => qc.clear(),
  })
}

// --- languages ------------------------------------------------------------

export function useLanguages() {
  return useQuery({
    queryKey: keys.languages,
    queryFn: () => api.get<Language[]>('/languages'),
  })
}

export function useLanguage(id: number) {
  return useQuery({
    queryKey: keys.language(id),
    queryFn: () => api.get<Language>(`/languages/${id}`),
    enabled: Number.isFinite(id),
  })
}

export function usePublicLanguages(q: string) {
  return useQuery({
    queryKey: keys.publicLanguages(q),
    queryFn: () =>
      api.get<Language[]>(
        `/languages/public${q ? `?q=${encodeURIComponent(q)}` : ''}`,
      ),
  })
}

export function useCreateLanguage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: {
      name: string
      description?: string
      source_language?: string
      visibility?: Visibility
    }) => api.post<Language>('/languages', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.languages }),
  })
}

export function useUpdateLanguage(id: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Partial<Pick<Language, 'name' | 'description' | 'visibility'>>) =>
      api.patch<Language>(`/languages/${id}`, body),
    onSuccess: (language) => {
      qc.setQueryData(keys.language(id), language)
      qc.invalidateQueries({ queryKey: keys.languages })
    },
  })
}

export function useDeleteLanguage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/languages/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.languages }),
  })
}

export function useCopyLanguage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.post<Language>(`/languages/${id}/copy`),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.languages }),
  })
}

// --- entries --------------------------------------------------------------

export function useEntries(languageId: number, q: string) {
  return useQuery({
    queryKey: keys.entries(languageId, q),
    queryFn: () =>
      api.get<EntryPage>(
        `/languages/${languageId}/entries?limit=500${q ? `&q=${encodeURIComponent(q)}` : ''}`,
      ),
    enabled: Number.isFinite(languageId),
  })
}

/** Invalidates everything derived from a language's word list. */
function useEntryMutation<TArgs>(
  languageId: number,
  fn: (args: TArgs) => Promise<unknown>,
) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['entries', languageId] })
      qc.invalidateQueries({ queryKey: keys.stats(languageId) })
      qc.invalidateQueries({ queryKey: keys.language(languageId) })
      qc.invalidateQueries({ queryKey: keys.languages })
    },
  })
}

export function useCreateEntry(languageId: number) {
  return useEntryMutation(languageId, (body: { source_text: string; target_text: string }) =>
    api.post<Entry>(`/languages/${languageId}/entries`, body),
  )
}

export function useUpdateEntry(languageId: number) {
  return useEntryMutation(
    languageId,
    ({ id, ...body }: { id: number; source_text?: string; target_text?: string }) =>
      api.patch<Entry>(`/entries/${id}`, body),
  )
}

export function useDeleteEntry(languageId: number) {
  return useEntryMutation(languageId, (id: number) => api.delete<void>(`/entries/${id}`))
}

export function useBulkImport(languageId: number) {
  return useEntryMutation(languageId, (text: string) =>
    api.post<BulkImportResult>(`/languages/${languageId}/entries/bulk`, { text }),
  ) as ReturnType<typeof useMutation<BulkImportResult, Error, string>>
}

// --- practice -------------------------------------------------------------

export function usePracticeQueue(languageId: number, direction: Direction | 'both') {
  return useQuery({
    queryKey: keys.practice(languageId, direction),
    queryFn: () =>
      api.get<PracticeCard[]>(
        `/languages/${languageId}/practice/next?limit=20${
          direction === 'both' ? '' : `&direction=${direction}`
        }`,
      ),
    enabled: Number.isFinite(languageId),
    // A session should always start from the server's current queue rather
    // than a queue cached from a previous visit.
    staleTime: 0,
    gcTime: 0,
  })
}

export function useAnswer() {
  return useMutation({
    mutationFn: (body: {
      entry_id: number
      direction: Direction
      given?: string
      grade?: number
    }) => api.post<AnswerResult>('/practice/answer', body),
  })
}

export function useStats(languageId: number) {
  return useQuery({
    queryKey: keys.stats(languageId),
    queryFn: () => api.get<LanguageStats>(`/languages/${languageId}/stats`),
    enabled: Number.isFinite(languageId),
  })
}
